/**
 * Shared activity-timeline primitives for the PAM module.
 *
 * Exports:
 *   - TimelineShell   — left-column icon + vertical connector line wrapper
 *   - buildTimeline   — merges comments + activity events, newest first
 *   - QuestionActivityItem — renders one question-level activity event
 *   - SaqActivityItem      — renders one SAQ-level activity event
 *   - authorName           — formats a comment author's display name
 */
import React from 'react';
import {
  ArrowsLeftRightIcon,
  CalendarIcon,
  CheckIcon,
  CheckSquareIcon,
  FilePlusIcon,
  FileXIcon,
  LinkIcon,
  ListPlusIcon,
  NoteBlankIcon,
  UserCircleIcon,
  UserIcon,
  UsersThreeIcon
} from '@phosphor-icons/react';

import { formatRelativeTime } from '@/lib/utils/formatRelativeTime';

import type { PamQuestionActivityEvent, PamSaqActivityEvent } from './api/types';

// ------------------------------------------------------------------ //
// Shared primitives                                                   //
// ------------------------------------------------------------------ //

/** Person names — medium weight. */
const name = (text: string) => <span className="font-medium text-content-default">{text}</span>;
/** Key values (titles, answers, dates, etc.) — italic. */
const val = (text: string) => <span className="italic text-content-default">{text}</span>;

// ------------------------------------------------------------------ //
// Timeline shell                                                      //
// ------------------------------------------------------------------ //

/**
 * Wraps any timeline item with a left column (icon circle + vertical
 * connector) and a right content column. Pass `isLast` on the final item
 * to omit the connector line.
 */
export function TimelineShell({
  icon,
  isLast,
  children
}: {
  icon: React.ReactNode;
  isLast: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <div className="flex shrink-0 flex-col items-center">
        {icon}
        {!isLast && <div className="w-px flex-1 bg-border-subtle" />}
      </div>
      <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-3'}`}>{children}</div>
    </li>
  );
}

// ------------------------------------------------------------------ //
// Timeline builder                                                    //
// ------------------------------------------------------------------ //

// ------------------------------------------------------------------ //
// Question activity                                                   //
// ------------------------------------------------------------------ //

// Concise label for an answer value, or null when there's nothing short to
// show. The answer type disambiguates a null value — an explicit N/A (a null
// KLEENEAN / PERCENTAGE) from cleared free text (a null TEXT) — which read
// identically from the value alone. TEXT answers (arbitrarily long strings)
// always return null so the caller falls back to generic phrasing ("edited the
// answer"). Events logged before answer_type was recorded fall back to
// value-shape inference, which can't recover the N/A label.
function answerValueLabel(answerType: unknown, value: unknown): string | null {
  switch (answerType) {
    case 'KLEENEAN':
      return value === true ? 'Yes' : value === false ? 'No' : 'N/A';
    case 'BOOLEAN':
      // Strict Boolean: Yes/No only, never N/A.
      return value === false ? 'No' : 'Yes';
    case 'PERCENTAGE':
      return typeof value === 'number' ? `${value}%` : 'N/A';
    case 'INTEGER':
      return typeof value === 'number' ? String(value) : 'N/A';
    case 'MATURITY':
      return typeof value === 'number' ? `Level ${value}` : 'N/A';
    case 'TEXT':
      return null;
  }
  // Legacy payloads without answer_type.
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (typeof value === 'number') return `${value}%`;
  return null;
}

function questionActivitySuffix(event_type: string, payload: Record<string, unknown> | null): React.ReactNode {
  switch (event_type) {
    case 'pam.question_final_answer_set': {
      if (payload?.cleared) return 'cleared the final answer';
      const answerLabel = answerValueLabel(payload?.answer_type, payload?.answer_value);
      return answerLabel === null ? 'set the final answer' : <>set the final answer to {val(answerLabel)}</>;
    }
    case 'pam.question_answer_scope_set':
      return payload?.answer_scope === 'TRUST'
        ? 'switched to a single trust-level answer'
        : 'switched to per-site answers';
    case 'pam.question_answer_added': {
      const answer = answerValueLabel(payload?.answer_type, payload?.answer_value);
      const site = typeof payload?.site_name === 'string' ? payload.site_name : null;
      if (answer === null) return site ? <>added an answer for {val(site)}</> : 'added an answer';
      return site ? (
        <>
          answered {val(answer)} for {val(site)}
        </>
      ) : (
        <>added a {val(answer)} answer</>
      );
    }
    case 'pam.question_answer_updated': {
      const answer = answerValueLabel(payload?.answer_type, payload?.answer_value);
      const site = typeof payload?.site_name === 'string' ? payload.site_name : null;
      if (answer === null) return site ? <>edited the answer for {val(site)}</> : 'edited the answer';
      return site ? (
        <>
          changed answer for {val(site)} to {val(answer)}
        </>
      ) : (
        <>changed answer to {val(answer)}</>
      );
    }
    case 'pam.question_answer_value_cleared': {
      const site = typeof payload?.site_name === 'string' ? payload.site_name : null;
      return site ? <>cleared the answer for {val(site)}</> : 'cleared the answer';
    }
    case 'pam.question_answer_removed': {
      const answer = answerValueLabel(payload?.answer_type, payload?.answer_value);
      const site = typeof payload?.site_name === 'string' ? payload.site_name : null;
      if (answer === null) return site ? <>removed the answer for {val(site)}</> : 'removed the answer';
      return site ? (
        <>
          removed {val(answer)} answer for {val(site)}
        </>
      ) : (
        <>removed a {val(answer)} answer</>
      );
    }
    case 'pam.question_evidence_uploaded': {
      const title = typeof payload?.document_title === 'string' ? payload.document_title : null;
      return title ? <>uploaded {val(`"${title}"`)}</> : 'uploaded evidence';
    }
    case 'pam.question_evidence_linked': {
      const titles = Array.isArray(payload?.document_titles) ? (payload.document_titles as string[]) : [];
      if (titles.length === 1) return <>linked {val(`"${titles[0]}"`)} as evidence</>;
      const count = typeof payload?.document_count === 'number' ? payload.document_count : titles.length;
      return (
        <>
          linked {val(String(count))} document{count !== 1 ? 's' : ''} as evidence
        </>
      );
    }
    case 'pam.question_other_evidence_added': {
      const label = typeof payload?.evidence_label === 'string' ? payload.evidence_label : null;
      return label ? <>added evidence: {val(`"${label}"`)}</> : 'added other evidence';
    }
    case 'pam.question_other_evidence_updated': {
      const label = typeof payload?.evidence_label === 'string' ? payload.evidence_label : null;
      return label ? <>updated evidence: {val(`"${label}"`)}</> : 'updated other evidence';
    }
    case 'pam.question_evidence_deleted': {
      const label = typeof payload?.evidence_label === 'string' ? payload.evidence_label : null;
      return label ? <>removed {val(`"${label}"`)}</> : 'removed evidence';
    }
    case 'pam.question_evidence_sites_set': {
      const title = typeof payload?.document_title === 'string' ? payload.document_title : null;
      const count = typeof payload?.site_count === 'number' ? payload.site_count : null;
      const sitePart =
        count !== null ? (
          <>
            {' '}
            across {val(String(count))} site{count !== 1 ? 's' : ''}
          </>
        ) : null;
      return title ? (
        <>
          updated sites for {val(`"${title}"`)}
          {sitePart}
        </>
      ) : (
        'updated evidence site coverage'
      );
    }
    case 'pam.question_assignees_replaced': {
      const names = Array.isArray(payload?.assignee_names) ? (payload.assignee_names as string[]) : [];
      return formatAssigneeNames(names, 'question');
    }
    case 'pam.question_deadline_set': {
      if (!payload?.deadline) return 'cleared the deadline';
      const date = new Date(payload.deadline as string);
      const formatted = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      return <>set a deadline for {val(formatted)}</>;
    }
    case 'pam.question_checklist_item_set': {
      const itemText = typeof payload?.item_text === 'string' ? payload.item_text : null;
      const verb = payload?.done ? 'checked' : 'unchecked';
      return itemText ? (
        <>
          {verb} {val(`"${itemText}"`)}
        </>
      ) : (
        `${verb} a checklist item`
      );
    }
    case 'pam.question_costed_action_added': {
      const ref = typeof payload?.reference_id === 'string' ? payload.reference_id : null;
      const desc = typeof payload?.description === 'string' ? payload.description : null;
      const cost = typeof payload?.cost === 'number' ? payload.cost : null;
      const costPart = cost !== null ? <> (£{cost.toLocaleString()})</> : null;
      if (ref && desc)
        return (
          <>
            added action {val(ref)}: {val(`"${desc}"`)}
            {costPart}
          </>
        );
      if (desc)
        return (
          <>
            added an action: {val(`"${desc}"`)}
            {costPart}
          </>
        );
      return <>added a costed action{costPart}</>;
    }
    default:
      return 'updated this question';
  }
}

function questionActivityIcon(event_type: string): React.ElementType {
  if (
    event_type === 'pam.question_evidence_uploaded' ||
    event_type === 'pam.question_evidence_linked' ||
    event_type === 'pam.question_other_evidence_added'
  )
    return FilePlusIcon;
  if (event_type === 'pam.question_other_evidence_updated' || event_type === 'pam.question_evidence_sites_set')
    return LinkIcon;
  if (event_type === 'pam.question_evidence_deleted') return FileXIcon;
  if (event_type === 'pam.question_assignees_replaced') return UserIcon;
  if (event_type === 'pam.question_deadline_set') return CalendarIcon;
  if (event_type === 'pam.question_answer_scope_set') return ArrowsLeftRightIcon;
  if (
    event_type === 'pam.question_final_answer_set' ||
    event_type === 'pam.question_answer_added' ||
    event_type === 'pam.question_answer_updated' ||
    event_type === 'pam.question_answer_value_cleared' ||
    event_type === 'pam.question_answer_removed' ||
    event_type === 'pam.question_checklist_item_set'
  )
    return CheckIcon;
  if (event_type === 'pam.question_costed_action_added') return ListPlusIcon;
  return NoteBlankIcon;
}

function ActivityIcon({ icon: Icon }: { icon: React.ElementType }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border-default text-content-subtle">
      <Icon size={16} weight="light" />
    </span>
  );
}

export function QuestionActivityItem({ event, isLast }: { event: PamQuestionActivityEvent; isLast: boolean }) {
  const Icon = questionActivityIcon(event.event_type);
  const actor = event.is_own ? 'You' : (event.actor_name ?? 'Someone');
  const suffix = questionActivitySuffix(event.event_type, event.payload);

  return (
    <TimelineShell isLast={isLast} icon={<ActivityIcon icon={Icon} />}>
      <div className="flex min-h-8 items-center justify-between gap-2">
        <p className="text-sm text-content-subtle">
          {name(actor)} {suffix}
        </p>
        <span className="shrink-0 text-xs text-content-subtle lining-nums">{formatRelativeTime(event.created_at)}</span>
      </div>
    </TimelineShell>
  );
}

// ------------------------------------------------------------------ //
// SAQ activity                                                        //
// ------------------------------------------------------------------ //

function formatAssigneeNames(names: string[], scope: 'question' | 'SAQ'): React.ReactNode {
  const clearMsg = scope === 'question' ? 'cleared the question assignees' : 'cleared the SAQ assignees';
  if (names.length === 0) return clearMsg;
  const LIMIT = 5;
  if (names.length <= LIMIT) {
    const parts = names.map((n, i) => (
      <React.Fragment key={i}>
        {i > 0 && (i === names.length - 1 ? ' and ' : ', ')}
        {name(n)}
      </React.Fragment>
    ));
    return <>set assignees to {parts}</>;
  }
  const shown = names.slice(0, LIMIT);
  const rest = names.length - LIMIT;
  return (
    <>
      set assignees to{' '}
      {shown.map((n, i) => (
        <React.Fragment key={i}>
          {i > 0 && ', '}
          {name(n)}
        </React.Fragment>
      ))}{' '}
      and {val(`${rest} other${rest === 1 ? '' : 's'}`)}
    </>
  );
}

function saqActivitySuffix(event_type: string, payload: Record<string, unknown> | null): React.ReactNode {
  switch (event_type) {
    case 'pam.saq_assignees_replaced': {
      const names = Array.isArray(payload?.assignee_names) ? (payload.assignee_names as string[]) : [];
      return formatAssigneeNames(names, 'SAQ');
    }
    case 'pam.saq_group_set': {
      const groupName = typeof payload?.group_name === 'string' ? payload.group_name : null;
      return groupName ? <>assigned group {val(`"${groupName}"`)}</> : 'removed the group assignment';
    }
    case 'pam.saq_deadline_set': {
      if (!payload?.deadline) return 'cleared the deadline';
      const date = new Date(payload.deadline as string);
      const formatted = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      return <>set a deadline for {val(formatted)}</>;
    }
    case 'pam.saq_complete_set':
      return payload?.complete ? 'marked this SAQ as complete' : 'marked this SAQ as incomplete';
    default:
      return 'updated this SAQ';
  }
}

function saqActivityIcon(event_type: string): React.ElementType {
  if (event_type === 'pam.saq_assignees_replaced') return UserCircleIcon;
  if (event_type === 'pam.saq_group_set') return UsersThreeIcon;
  if (event_type === 'pam.saq_deadline_set') return CalendarIcon;
  return CheckSquareIcon;
}

export function SaqActivityItem({ event, isLast }: { event: PamSaqActivityEvent; isLast: boolean }) {
  const isQuestionEvent = event.question_ref !== null;
  const Icon = isQuestionEvent ? questionActivityIcon(event.event_type) : saqActivityIcon(event.event_type);
  const actor = event.is_own ? 'You' : (event.actor_name ?? 'Someone');
  const suffix = isQuestionEvent
    ? questionActivitySuffix(event.event_type, event.payload)
    : saqActivitySuffix(event.event_type, event.payload);

  return (
    <TimelineShell isLast={isLast} icon={<ActivityIcon icon={Icon} />}>
      <div className="flex min-h-8 items-center justify-between gap-2">
        <p className="text-sm text-content-subtle">
          {isQuestionEvent && event.question_ref ? (
            <>
              {name(actor)} {suffix} for {val(event.question_ref)}
            </>
          ) : (
            <>
              {name(actor)} {suffix}
            </>
          )}
        </p>
        <span className="shrink-0 text-xs text-content-subtle lining-nums">{formatRelativeTime(event.created_at)}</span>
      </div>
    </TimelineShell>
  );
}
